import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion, useMotionValue, useReducedMotion } from 'framer-motion';
import { ArrowRightIcon, ChevronLeftIcon, ChevronRightIcon, PauseIcon, PlayIcon } from 'lucide-react';
import { MockupTag } from '../ui/MockupTag';
import { heroSlides } from '../../data/heroSlides';
const ease = [0.23, 1, 0.32, 1] as const;
const SLIDE_MS = 7000;

// Full-bleed, auto-advancing story hero: crossfading photos, masked headline reveals and a progress pill.
export function HomeHero() {
  const reduceMotion = useReducedMotion();
  const total = heroSlides.length;
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [hovering, setHovering] = useState(false);
  const progress = useMotionValue(0);
  const running = !paused && !hovering;
  const slide = heroSlides[index];
  const go = useCallback((next: number) => {
    progress.set(0);
    setIndex((next + total) % total);
  }, [progress, total]);
  useEffect(() => {
    if (!running) return;
    let frame = 0;
    let last = performance.now();
    const tick = (now: number) => {
      const next = progress.get() + (now - last) / SLIDE_MS;
      last = now;
      if (next >= 1) {
        progress.set(0);
        setIndex(i => (i + 1) % total);
      } else {
        progress.set(next);
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [running, progress, total]);
  const enter = (delay: number) => ({
    initial: reduceMotion ? {
      opacity: 0
    } : {
      opacity: 0,
      y: 24
    },
    animate: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.28,
        delay,
        ease
      }
    },
    exit: {
      opacity: 0,
      y: reduceMotion ? 0 : -10,
      transition: {
        duration: 0.15,
        ease
      }
    }
  });
  return <section className="w-full bg-white p-2 md:p-4" aria-roledescription="carousel" aria-label="Discover ES World" data-id="element-477" data-mp="3ec6embdhl6vm">
      <h1 className="sr-only" data-id="element-478" data-mp="1hclo0g3wk5tt">ES World English courses for adults, teens and professionals</h1>
      <div className="relative flex h-[calc(100svh-88px)] min-h-[600px] flex-col overflow-hidden rounded-[28px] bg-ink md:h-[calc(100svh-104px)] md:rounded-[40px]" onMouseEnter={() => setHovering(true)} onMouseLeave={() => setHovering(false)} data-id="element-479" data-mp="1mboix7q64vo2">
        {heroSlides.map((item, i) => {
        const active = i === index;
        return <motion.img key={item.image} src={item.image} alt={active ? item.alt : ''} aria-hidden={!active} className="absolute inset-0 h-full w-full object-cover" initial={false} animate={{
          opacity: active ? 1 : 0,
          scale: active || reduceMotion ? 1 : 1.08
        }} transition={{
          opacity: {
            duration: 0.3,
            ease: 'easeOut'
          },
          scale: {
            duration: active ? SLIDE_MS / 1000 : 0,
            ease: 'linear'
          }
        }} data-id="element-480" data-mp="258zh0p02dj1x" data-mpi={i} />;
      })}
        <div className="absolute inset-0 bg-black/35" aria-hidden="true" data-id="element-481" data-mp="8b1wsyym8agy" />
        <MockupTag note="Placeholder images" className="absolute right-5 top-5 z-10" data-id="element-482" data-mp="8d4ve7du3bxa" />

        <div className="relative z-10 mx-auto flex w-full max-w-content flex-1 flex-col justify-end px-6 pb-40 pt-16 md:px-10 md:pb-28" aria-live={running ? 'off' : 'polite'} data-id="element-483" data-mp="3ch2th9yin0y">
          <AnimatePresence mode="wait" data-id="element-484" data-mp="vzteuxihrcg0">
            <motion.div key={index} className="max-w-xl" aria-roledescription="slide" aria-label={`${index + 1} of ${total}`} data-id="element-485" data-mp="r1rvscl2fd4j">
              <motion.p {...enter(0)} className="flex items-center gap-2 type-small font-bold text-white" data-id="element-486" data-mp="m9ruafpckc5u">
                <span className="h-2 w-2 rounded-full bg-brand" aria-hidden="true" data-id="element-487" data-mp="haxcnh348tnb" />
                {String(index + 1).padStart(2, '0')} / {String(total).padStart(2, '0')}
              </motion.p>

              <div className="mt-3 overflow-hidden pb-1" data-id="element-488" data-mp="19zm56rq3vqng">
                <motion.h2 initial={reduceMotion ? {
                opacity: 0
              } : {
                y: '100%'
              }} animate={{
                opacity: 1,
                y: 0,
                transition: {
                  duration: 0.3,
                  delay: 0.04,
                  ease
                }
              }} exit={{
                opacity: 0,
                transition: {
                  duration: 0.15
                }
              }} className="type-h2 text-white" data-id="element-489" data-mp="uime7qj1bpkw">
                  {slide.title}
                </motion.h2>
              </div>

              <motion.p {...enter(0.1)} className="mt-3 max-w-md type-body text-white" data-id="element-490" data-mp="1d8m24a352ymq">
                {slide.subtitle}
              </motion.p>

              <motion.div {...enter(0.16)} className="mt-6" data-id="element-491" data-mp="13jue2d178yh3">
                <Link to={slide.cta.to} className="group inline-flex h-12 items-center gap-3 whitespace-nowrap rounded-[15px] bg-brand px-6 type-button font-bold text-white transition-colors duration-150 hover:bg-brand-hover active:bg-brand-press focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-white/60" data-id="element-492" data-mp="13h864caecgyc">
                  {slide.cta.label}
                  <ArrowRightIcon className="h-5 w-5 transition-transform duration-150 ease-brand group-hover:translate-x-0.5" aria-hidden="true" data-id="element-493" data-mp="3gpsg9g8bh73b" />
                </Link>
              </motion.div>
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="absolute inset-x-0 bottom-0 z-10 mx-auto flex w-full max-w-content items-center justify-between px-6 pb-24 md:px-10 md:pb-9" data-id="element-494" data-mp="32phng7ng5ifi">
          <div className="flex items-center gap-4" data-id="element-495" data-mp="2xszjvb3urq4r">
            <div className="flex items-center gap-2" role="group" aria-label="Choose a slide" data-id="element-496" data-mp="3bqxv47mvn6g1">
              {heroSlides.map((item, i) => {
              const active = i === index;
              return <button key={item.title} type="button" onClick={() => go(i)} aria-label={`Show slide ${i + 1}: ${item.title}`} aria-current={active} className={`relative h-2.5 overflow-hidden rounded-full transition-[width,background-color] duration-300 ease-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white ${active ? 'w-10 bg-white/35' : 'w-2.5 border-2 border-white bg-transparent hover:bg-white/50'}`} data-id="element-497" data-mp="qz5igoes4n82" data-mpi={i}>
                    {active && <motion.span className="absolute inset-0 origin-left rounded-full bg-brand" style={{
                  scaleX: progress
                }} data-id="element-498" data-mp="314fn5xqu1yfs" />}
                  </button>;
            })}
            </div>
            <button type="button" onClick={() => setPaused(p => !p)} aria-label={paused ? 'Play slideshow' : 'Pause slideshow'} className="flex h-9 w-9 items-center justify-center rounded-full bg-white/15 text-white transition-colors duration-150 hover:bg-white/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white" data-id="element-499" data-mp="3auikub8wg0kg">
              {paused ? <PlayIcon className="h-4 w-4" data-id="element-500" data-mp="25wnbc5lykh6k" /> : <PauseIcon className="h-4 w-4" data-id="element-501" data-mp="25tj4f0h3z7c7" />}
            </button>
          </div>

          <div className="hidden items-center gap-2 md:flex" data-id="element-502" data-mp="20ufzrh51j34r">
            <button type="button" onClick={() => go(index - 1)} aria-label="Previous slide" className="flex h-12 w-12 items-center justify-center rounded-[15px] bg-white/15 text-white transition-colors duration-150 hover:bg-white hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white" data-id="element-503" data-mp="1wtm9ih44lc8p">
              <ChevronLeftIcon className="h-5 w-5" data-id="element-504" data-mp="1d7g6kovwuvcv" />
            </button>
            <button type="button" onClick={() => go(index + 1)} aria-label="Next slide" className="flex h-12 w-12 items-center justify-center rounded-[15px] bg-white/15 text-white transition-colors duration-150 hover:bg-white hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white" data-id="element-505" data-mp="19539eov8c5rp">
              <ChevronRightIcon className="h-5 w-5" data-id="element-506" data-mp="2wzewg4znd0zf" />
            </button>
          </div>
        </div>
      </div>
    </section>;
}